import type { Aggregation, DimensionRole, LogicalDataType, TimeGrain } from '@aibi/contracts';
import {
  isCalculatedDimension,
  isCalculatedMeasure,
  isGeographicDimension,
  isTemporalDimension,
  type SemanticModelIndex,
} from '@aibi/semantic-model';

export type FieldKind = 'dimension' | 'measure';

/** Rol visible de un campo en el explorador (determina icono y agrupacion). */
export type FieldRole = DimensionRole | 'measure';

/**
 * Campo del Semantic Model listo para el explorador. Es una proyeccion de solo lectura:
 * el explorador nunca modifica el modelo.
 */
export interface FieldDescriptor {
  readonly id: string;
  readonly kind: FieldKind;
  readonly name: string;
  readonly description: string | null;
  /** Tabla de origen (carpeta en el explorador); `null` en medidas calculadas sin tabla. */
  readonly folder: string | null;
  readonly role: FieldRole;
  readonly dataType: LogicalDataType;
  readonly temporal: boolean;
  readonly geographic: boolean;
  readonly calculated: boolean;
  readonly hierarchyId: string | null;
  readonly defaultTimeGrain: TimeGrain | null;
  readonly aggregation: Aggregation | null;
  readonly synonyms: readonly string[];
}

export interface FieldCatalog {
  readonly dimensions: readonly FieldDescriptor[];
  readonly measures: readonly FieldDescriptor[];
  get(fieldId: string): FieldDescriptor | undefined;
}

const NUMERIC_AGGREGATIONS: ReadonlySet<string> = new Set(['count', 'count-distinct']);

/** Construye el catalogo de campos visibles (excluye los marcados como ocultos). */
export function buildFieldCatalog(index: SemanticModelIndex): FieldCatalog {
  const { model } = index;
  const byId = new Map<string, FieldDescriptor>();
  const dimensions: FieldDescriptor[] = [];
  const measures: FieldDescriptor[] = [];

  for (const dimension of [...model.dimensions, ...model.calculatedDimensions]) {
    if (dimension.hidden) continue;
    const resolved = index.resolveDimension(dimension.id);
    if (!resolved) continue;
    const descriptor: FieldDescriptor = {
      id: dimension.id,
      kind: 'dimension',
      name: dimension.label ?? dimension.name,
      description: dimension.description ?? null,
      folder: resolved.table.label ?? resolved.table.name,
      role: resolved.role,
      dataType: resolved.dataType,
      temporal: isTemporalDimension(resolved),
      geographic: isGeographicDimension(resolved),
      calculated: isCalculatedDimension(dimension),
      hierarchyId: dimension.hierarchyId ?? null,
      defaultTimeGrain:
        'defaultTimeGrain' in dimension ? (dimension.defaultTimeGrain ?? null) : null,
      aggregation: null,
      synonyms: dimension.synonyms,
    };
    dimensions.push(descriptor);
    byId.set(descriptor.id, descriptor);
  }

  for (const measure of [...model.measures, ...model.calculatedMeasures]) {
    if (measure.hidden) continue;
    const calculated = isCalculatedMeasure(measure);
    const aggregation = calculated ? null : measure.aggregation;
    const tableId = calculated
      ? (measure.tableId ?? null)
      : (measure.tableId ??
        (measure.columnId === undefined
          ? null
          : (index.getColumn(measure.columnId)?.table.id ?? null)));
    const table = tableId === null ? undefined : index.getTable(tableId);
    const descriptor: FieldDescriptor = {
      id: measure.id,
      kind: 'measure',
      name: measure.label ?? measure.name,
      description: measure.description ?? null,
      folder: table ? (table.label ?? table.name) : null,
      role: 'measure',
      dataType:
        aggregation !== null && NUMERIC_AGGREGATIONS.has(aggregation) ? 'integer' : 'decimal',
      temporal: false,
      geographic: false,
      calculated,
      hierarchyId: null,
      defaultTimeGrain: null,
      aggregation,
      synonyms: measure.synonyms,
    };
    measures.push(descriptor);
    byId.set(descriptor.id, descriptor);
  }

  return { dimensions, measures, get: (fieldId) => byId.get(fieldId) };
}

const normalize = (text: string): string =>
  text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/** Busqueda insensible a mayusculas y acentos por nombre, descripcion, carpeta o sinonimos. */
export function searchFields(
  fields: readonly FieldDescriptor[],
  query: string,
): readonly FieldDescriptor[] {
  const needle = normalize(query.trim());
  if (needle === '') return fields;
  return fields.filter((field) =>
    [field.name, field.description ?? '', field.folder ?? '', ...field.synonyms].some((text) =>
      normalize(text).includes(needle),
    ),
  );
}
